import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MetaHead } from '../components/MetaHead';
import { SCRAPED_PAGES, COMMON_CONTACT_CALLOUT, COMPANY_INFO } from '../data/scrapedData';

export function WhoWeAre() {
  const p = SCRAPED_PAGES.hvemErVi;
  const [selectedPerson, setSelectedPerson] = useState(null);

  useEffect(() => {
    if (selectedPerson) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e) => {
        if (e.key === 'Escape') setSelectedPerson(null);
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = 'auto';
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [selectedPerson]);

  return (
    <>
      <MetaHead title={p.title} description={p.metaDescription} />

      <section className="bg-[#1C1C1E] text-white py-14 sm:py-18">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs uppercase tracking-widest font-semibold text-[#3885B8] block mb-2">
            Den Selvejende Institution Midtgaarden
          </span>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white mb-3">
            {p.h1}
          </h1>
          <p className="text-base sm:text-lg text-white/90 max-w-3xl leading-relaxed">
            {p.intro}
          </p>
        </div>
      </section>

      {/* Main Staff & Organisation Grid */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          
          {/* Quick Sub-navigation */}
          <div className="flex flex-wrap gap-2 pb-6 border-b border-[#1C1C1E]/10">
            <a href="#ledelse" className="px-3.5 py-1.5 bg-white text-xs font-semibold text-[#1C1C1E] rounded-lg border border-[#1C1C1E]/10 hover:bg-[#1C1C1E] hover:text-white transition-colors">Ledelse</a>
            <a href="#raadgivere" className="px-3.5 py-1.5 bg-white text-xs font-semibold text-[#1C1C1E] rounded-lg border border-[#1C1C1E]/10 hover:bg-[#1C1C1E] hover:text-white transition-colors">Rådgivere & Behandlere</a>
            <a href="#sundhed" className="px-3.5 py-1.5 bg-white text-xs font-semibold text-[#1C1C1E] rounded-lg border border-[#1C1C1E]/10 hover:bg-[#1C1C1E] hover:text-white transition-colors">Psykiater, Sygeplejerske & Psykolog</a>
            <a href="#oevrig" className="px-3.5 py-1.5 bg-white text-xs font-semibold text-[#1C1C1E] rounded-lg border border-[#1C1C1E]/10 hover:bg-[#1C1C1E] hover:text-white transition-colors">Øvrige medarbejdere</a>
          </div>

          {/* Ledelse Section */}
          <div id="ledelse" className="space-y-6">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4 border-l-4 border-[#1C1C1E] pl-3">
              Ledelse
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {p.leadership.map((item) => {
                const hasMore = item.details && item.details.length > 4;
                const visibleDetails = hasMore ? item.details.slice(0, 3) : item.details;

                return (
                  <div key={item.name} className="bg-white p-6 rounded-2xl border border-[#1C1C1E]/10 shadow-xs flex flex-col justify-between space-y-4">
                    <div className="space-y-4">
                      {/* Image above text, 130px on desktop */}
                      {item.image && (
                        <img
                          src={`https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=85,format=auto/${item.image.replace('https://assets.auxodev.dk/', '')}`}
                          alt={`Portrætfoto af ${item.name}`}
                          loading="lazy"
                          className="w-28 h-28 md:w-[130px] md:h-[130px] object-cover rounded-xl shrink-0"
                        />
                      )}
                      <div>
                        <span className="text-xs uppercase tracking-wider text-[#3885B8] font-bold block mb-1">
                          {item.title}
                        </span>
                        <h3 className="text-xl font-bold text-[#1C1C1E]">{item.name}</h3>
                        {item.email && (
                          <a href={`mailto:${item.email}`} className="text-xs font-semibold text-[#3885B8] hover:underline whitespace-nowrap inline-block mt-1">
                            <i className="fa-solid fa-envelope mr-1" aria-hidden="true" />
                            {item.email}
                          </a>
                        )}
                      </div>
                      <ul className="space-y-1.5 pt-3 border-t border-[#1C1C1E]/10">
                        {visibleDetails.map((detail, idx) => (
                          <li key={idx} className="text-xs text-[#1C1C1E]/80 flex items-start gap-2">
                            <i className="fa-solid fa-check text-[#3885B8] text-[10px] mt-1 shrink-0" aria-hidden="true" />
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    {hasMore && (
                      <div className="pt-2">
                        <button
                          onClick={() => setSelectedPerson(item)}
                          className="text-xs font-bold text-[#3885B8] hover:text-[#2A6E9B] hover:underline inline-flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-[#3885B8] rounded-md px-1 py-0.5"
                        >
                          Læs mere
                          <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Rådgivere & Behandlere */}
          <div id="raadgivere" className="space-y-6">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4 border-l-4 border-[#1C1C1E] pl-3">
              Rådgivere / Behandlere
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {p.advisors.map((person) => {
                const hasMore = person.details && person.details.length > 4;
                const visibleDetails = hasMore ? person.details.slice(0, 3) : person.details;

                return (
                  <div key={person.name} className="bg-white p-6 rounded-2xl border border-[#1C1C1E]/10 shadow-xs flex flex-col justify-between space-y-4">
                    <div className="space-y-3">
                      {/* Image above text, 130px on desktop */}
                      {person.image && (
                        <img
                          src={`https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=85,format=auto/${person.image.replace('https://assets.auxodev.dk/', '')}`}
                          alt={`Portrætfoto af ${person.name}`}
                          loading="lazy"
                          className="w-28 h-28 md:w-[130px] md:h-[130px] object-cover rounded-xl shrink-0"
                        />
                      )}
                      <div>
                        <h3 className="text-lg font-bold text-[#1C1C1E]">{person.name}</h3>
                        {person.email && (
                          <a href={`mailto:${person.email}`} className="text-xs font-semibold text-[#3885B8] hover:underline whitespace-nowrap inline-block mt-0.5">
                            <i className="fa-solid fa-envelope mr-1" aria-hidden="true" />
                            {person.email}
                          </a>
                        )}
                      </div>
                      <ul className="space-y-1.5 pt-3 border-t border-[#1C1C1E]/10">
                        {visibleDetails.map((d, idx) => (
                          <li key={idx} className="text-xs text-[#1C1C1E]/80 flex items-start gap-2">
                            <i className="fa-solid fa-circle text-[6px] text-[#3885B8] mt-1.5 shrink-0" aria-hidden="true" />
                            <span>{d}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    {hasMore && (
                      <div className="pt-2">
                        <button
                          onClick={() => setSelectedPerson(person)}
                          className="text-xs font-bold text-[#3885B8] hover:text-[#2A6E9B] hover:underline inline-flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-[#3885B8] rounded-md px-1 py-0.5"
                        >
                          Læs mere
                          <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sundhedspersonale & Psykolog */}
          <div id="sundhed" className="space-y-6">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4 border-l-4 border-[#1C1C1E] pl-3">
              Sundhedspersonale & Psykolog
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {p.healthStaff.map((person) => {
                const hasMore = person.details && person.details.length > 4;
                const visibleDetails = hasMore ? person.details.slice(0, 3) : person.details;

                return (
                  <div key={person.name} className="bg-white p-6 rounded-2xl border border-[#1C1C1E]/10 shadow-xs flex flex-col justify-between space-y-4">
                    <div className="space-y-3">
                      {/* Image above text, 130px on desktop */}
                      {person.image && (
                        <img
                          src={`https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=85,format=auto/${person.image.replace('https://assets.auxodev.dk/', '')}`}
                          alt={`Portrætfoto af ${person.name}`}
                          loading="lazy"
                          className="w-28 h-28 md:w-[130px] md:h-[130px] object-cover rounded-xl shrink-0"
                        />
                      )}
                      <div>
                        {person.title && (
                          <span className="text-xs font-semibold text-[#3885B8] uppercase block mb-0.5">
                            {person.title}
                          </span>
                        )}
                        <h3 className="text-lg font-bold text-[#1C1C1E]">{person.name}</h3>
                        {person.email && (
                          <a href={`mailto:${person.email}`} className="text-xs font-semibold text-[#3885B8] hover:underline whitespace-nowrap inline-block mt-0.5">
                            <i className="fa-solid fa-envelope mr-1" aria-hidden="true" />
                            {person.email}
                          </a>
                        )}
                      </div>
                      <ul className="space-y-1.5 pt-3 border-t border-[#1C1C1E]/10">
                        {visibleDetails.map((d, idx) => (
                          <li key={idx} className="text-xs text-[#1C1C1E]/80 flex items-start gap-2">
                            <i className="fa-solid fa-stethoscope text-[#3885B8] text-[10px] mt-1 shrink-0" aria-hidden="true" />
                            <span>{d}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    {hasMore && (
                      <div className="pt-2">
                        <button
                          onClick={() => setSelectedPerson(person)}
                          className="text-xs font-bold text-[#3885B8] hover:text-[#2A6E9B] hover:underline inline-flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-[#3885B8] rounded-md px-1 py-0.5"
                        >
                          Læs mere
                          <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Nattevagter & Øvrige */}
          <div id="oevrig" className="space-y-12">
            <div className="space-y-6">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4 border-l-4 border-[#1C1C1E] pl-3">
                Nattevagter
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {p.nightStaff.map((person) => {
                  const hasMore = person.details && person.details.length > 4;
                  const visibleDetails = hasMore ? person.details.slice(0, 3) : person.details;

                  return (
                    <div key={person.name} className="bg-white p-6 rounded-2xl border border-[#1C1C1E]/10 shadow-xs flex flex-col justify-between space-y-4">
                      <div className="space-y-3">
                        {person.image && (
                          <img
                            src={`https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=85,format=auto/${person.image.replace('https://assets.auxodev.dk/', '')}`}
                            alt={`Portrætfoto af ${person.name}`}
                            loading="lazy"
                            className="w-28 h-28 md:w-[130px] md:h-[130px] object-cover rounded-xl shrink-0"
                          />
                        )}
                        <div>
                          <h3 className="font-bold text-[#1C1C1E] text-base">{person.name}</h3>
                          <ul className="space-y-1 mt-2 pt-2 border-t border-[#1C1C1E]/10">
                            {visibleDetails.map((d, idx) => (
                              <li key={idx} className="text-xs text-[#1C1C1E]/80 flex items-start gap-1.5">
                                <i className="fa-solid fa-circle text-[5px] text-[#3885B8] mt-1.5 shrink-0" aria-hidden="true" />
                                <span>{d}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      {hasMore && (
                        <div>
                          <button
                            onClick={() => setSelectedPerson(person)}
                            className="text-xs font-bold text-[#3885B8] hover:text-[#2A6E9B] hover:underline inline-flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-[#3885B8] rounded-md px-1 py-0.5"
                          >
                            Læs mere
                            <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-6">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4 border-l-4 border-[#1C1C1E] pl-3">
                Øvrige medarbejdere
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {p.otherStaff.map((person) => {
                  const hasMore = person.details && person.details.length > 4;
                  const visibleDetails = hasMore ? person.details.slice(0, 3) : person.details;

                  return (
                    <div key={person.name} className="bg-white p-6 rounded-2xl border border-[#1C1C1E]/10 shadow-xs flex flex-col justify-between space-y-4">
                      <div className="space-y-3">
                        {person.image && (
                          <img
                            src={`https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=85,format=auto/${person.image.replace('https://assets.auxodev.dk/', '')}`}
                            alt={`Portrætfoto af ${person.name}`}
                            loading="lazy"
                            className="w-28 h-28 md:w-[130px] md:h-[130px] object-cover rounded-xl shrink-0"
                          />
                        )}
                        <div>
                          {person.role && (
                            <span className="text-[11px] font-bold text-[#3885B8] uppercase block">
                              {person.role}
                            </span>
                          )}
                          <h3 className="font-bold text-[#1C1C1E] text-sm">{person.name}</h3>
                          {person.email && (
                            <a href={`mailto:${person.email}`} className="text-xs text-[#3885B8] hover:underline block whitespace-nowrap mt-0.5">
                              {person.email}
                            </a>
                          )}
                        </div>
                        <ul className="space-y-1 pt-2 border-t border-[#1C1C1E]/10">
                          {visibleDetails.map((d, idx) => (
                            <li key={idx} className="text-xs text-[#1C1C1E]/75 flex items-start gap-1.5">
                              <i className="fa-solid fa-circle text-[5px] text-[#3885B8] mt-1.5 shrink-0" aria-hidden="true" />
                              <span>{d}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      {hasMore && (
                        <div>
                          <button
                            onClick={() => setSelectedPerson(person)}
                            className="text-xs font-bold text-[#3885B8] hover:text-[#2A6E9B] hover:underline inline-flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-[#3885B8] rounded-md px-1 py-0.5"
                          >
                            Læs mere
                            <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Modal Popup for Full Profile */}
      {selectedPerson && (
        <div 
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto animate-in fade-in duration-200"
          onClick={() => setSelectedPerson(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-person-name"
        >
          <div 
            className="bg-white rounded-2xl max-w-lg w-full p-6 sm:p-8 shadow-2xl relative space-y-5 my-8 border border-[#1C1C1E]/10"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedPerson(null)}
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center text-[#1C1C1E]/60 hover:text-[#1C1C1E] bg-slate-100 hover:bg-slate-200 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#3885B8]"
              aria-label="Luk vindue"
            >
              <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
            </button>

            <div className="flex flex-col sm:flex-row items-start gap-4 pr-8">
              {selectedPerson.image && (
                <img
                  src={`https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=85,format=auto/${selectedPerson.image.replace('https://assets.auxodev.dk/', '')}`}
                  alt={`Portrætfoto af ${selectedPerson.name}`}
                  className="w-24 h-24 sm:w-28 sm:h-28 object-cover rounded-xl border border-[#1C1C1E]/10 shrink-0"
                />
              )}
              <div className="space-y-1">
                {(selectedPerson.title || selectedPerson.role) && (
                  <span className="text-xs uppercase tracking-wider text-[#3885B8] font-bold block">
                    {selectedPerson.title || selectedPerson.role}
                  </span>
                )}
                <h2 id="modal-person-name" className="text-2xl font-bold text-[#1C1C1E]">
                  {selectedPerson.name}
                </h2>
                {selectedPerson.email && (
                  <a 
                    href={`mailto:${selectedPerson.email}`} 
                    className="text-xs font-semibold text-[#3885B8] hover:underline inline-flex items-center gap-1 mt-1"
                  >
                    <i className="fa-solid fa-envelope" aria-hidden="true" />
                    {selectedPerson.email}
                  </a>
                )}
              </div>
            </div>

            <div className="border-t border-[#1C1C1E]/10 pt-4 space-y-3">
              <h3 className="text-xs uppercase font-bold tracking-wider text-[#1C1C1E]/60">
                Uddannelse, erfaring & kompetencer
              </h3>
              <ul className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                {selectedPerson.details.map((detail, idx) => (
                  <li key={idx} className="text-xs sm:text-sm text-[#1C1C1E]/85 flex items-start gap-2.5 leading-relaxed">
                    <i className="fa-solid fa-circle-check text-[#3885B8] text-xs mt-1 shrink-0" aria-hidden="true" />
                    <span>{detail}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="pt-3 border-t border-[#1C1C1E]/10 flex justify-end">
              <button
                onClick={() => setSelectedPerson(null)}
                className="px-5 py-2.5 rounded-full bg-[#1C1C1E] text-white text-xs font-bold hover:bg-[#3885B8] transition-colors focus:outline-none focus:ring-2 focus:ring-[#3885B8]"
              >
                Luk profil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Callout */}
      <section className="py-12 bg-white border-t border-[#1C1C1E]/10">
        <div className="max-w-7xl mx-auto px-4 text-center space-y-4">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">{COMMON_CONTACT_CALLOUT.header}</h2>
          <p className="text-sm text-[#1C1C1E]/80 leading-relaxed">{COMMON_CONTACT_CALLOUT.p1}</p>
          <div className="pt-2 flex justify-center gap-4">
            <a href={COMPANY_INFO.phoneTel} className="bg-[#3885B8] hover:bg-[#2A6E9B] text-white px-5 py-2.5 rounded-lg font-semibold text-sm whitespace-nowrap">
              Ring {COMPANY_INFO.phone}
            </a>
            <a href={COMPANY_INFO.emailMailto} className="border border-[#3885B8] text-[#3885B8] hover:bg-[#3885B8]/10 px-5 py-2.5 rounded-lg font-semibold text-sm whitespace-nowrap">
              Skriv til os
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
